/**
 * Monthly reading chart component that displays water consumption data in a bar chart format.
 * Features internationalized month labels and consumption values.
 */

import { Bar } from "react-chartjs-2";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useMemo } from "react";

/**
 * MonthlyReadingChart component that renders a bar chart of monthly water consumption data.
 * Includes translated month labels and formatted consumption values.
 *
 * @param {Object} props - Component props
 * @param {Object} props.data - Chart data object containing labels and datasets
 * @returns {JSX.Element} The rendered MonthlyReadingChart component
 */
export const MonthlyReadingChart = ({ data }) => {
  const { t } = useTranslation();

  // Safely translate month labels only if they are parseable dates; otherwise keep as-is
  const translatedData = useMemo(() => {
    const monthKeys = [
      "january",
      "february",
      "march",
      "april",
      "may",
      "june",
      "july",
      "august",
      "september",
      "october",
      "november",
      "december",
    ];

    const labels = (data.labels || []).map((label) => {
      const d = new Date(label);
      if (isNaN(d)) {
        // Label is already a formatted string (e.g., 'Tammikuu 2025'); keep it
        return label;
      }
      return `${t(monthKeys[d.getMonth()])} ${d.getFullYear()}`;
    });

    return {
      ...data,
      labels,
      datasets: (data.datasets || []).map((dataset) => ({
        ...dataset,
        label: t("montly_consumption"),
      })),
    };
  }, [data, t]);

  /**
   * Chart configuration options including scales, plugins, and tooltips
   */
  const options = {
    responsive: true,
    maintainAspectRatio: false,
    scales: {
      y: {
        beginAtZero: true,
        min: 0,
        grace: "0%",
        ticks: { precision: 0 },
        title: {
          display: true,
          text: t("monthly_consumption_difference"),
        },
      },
      x: {
        title: {
          display: true,
          text: t("month"),
        },
      },
    },
    plugins: {
      legend: {
        position: "top",
        labels: {
          font: { size: 12 },
        },
      },
      title: {
        display: true,
        text: t("monthly_reading"),
        font: { size: 16, weight: "bold" },
      },
      tooltip: {
        callbacks: {
          title: (tooltipItems) => tooltipItems[0].label,
          label: (context) => {
            const value = context.parsed.y;
            return `${t("montly_consumption")}: ${Number(
              value
            ).toLocaleString()}`;
          },
        },
      },
    },
  };

  return (
    <div style={{ width: "100%", height: "100%" }}>
      <Bar data={translatedData} options={options} />
    </div>
  );
};

// PropTypes validation for MonthlyReadingChart component
MonthlyReadingChart.propTypes = {
  data: PropTypes.shape({
    labels: PropTypes.arrayOf(PropTypes.string).isRequired,
    datasets: PropTypes.arrayOf(
      PropTypes.shape({
        label: PropTypes.string,
        data: PropTypes.arrayOf(PropTypes.number).isRequired,
        backgroundColor: PropTypes.string,
        borderColor: PropTypes.string,
        borderWidth: PropTypes.number,
      })
    ).isRequired,
  }).isRequired,
  onToggleChart: PropTypes.func.isRequired,
};
